import { useState } from "react";
import { Image, View } from "react-native";

type PreviewImageProps = Readonly<{
  uri: string | null;
  /** Width-to-height ratio of the slot the image fills. */
  aspectRatio: number;
  testID?: string;
  /**
   * Rendered in the slot when the image fails to load (the Selisian app shows a
   * neutral box with a link icon). Without it the slot collapses.
   */
  fallback?: React.ReactNode;
}>;

/**
 * The image half of a preview card.
 *
 * A remote `og:image` is someone else's URL: it can 404, time out, or have been
 * replaced by something that is not an image at all. A missing image renders no
 * slot (text-only card); a broken one keeps the slot with `fallback`, so the card
 * does not jump while the feed is being read.
 */
const PreviewImage = ({ uri, aspectRatio, testID, fallback }: PreviewImageProps) => {
  const [failed, setFailed] = useState(false);

  if (!uri) return null;
  if (failed) {
    return fallback ? (
      <View
        style={{ width: "100%", aspectRatio }}
        className="bg-ash-3 items-center justify-center"
        testID={testID ? `${testID}-fallback` : undefined}
      >
        {fallback}
      </View>
    ) : null;
  }

  return (
    <View style={{ width: "100%", aspectRatio }} className="bg-ash-3">
      <Image
        source={{ uri }}
        style={{ width: "100%", height: "100%" }}
        resizeMode="cover"
        onError={() => setFailed(true)}
        accessibilityIgnoresInvertColors
        testID={testID}
      />
    </View>
  );
};

export default PreviewImage;
